/**
 * What ClaimHive pulled out of a claim and what to do next, for the first-claim
 * reveal. Plain rules over the claim's own fields: no AI model is called, so no
 * claim data leaves the app here.
 */

export interface FirstClaimFields {
  claimNumber?: string | null;
  policyNumber?: string | null;
  dateOfLoss?: string | null;
  perilType?: string | null;
  claimType?: string | null;
  policyholderFirstName?: string | null;
  policyholderLastName?: string | null;
  policyholderCompany?: string | null;
  policyholderEmail?: string | null;
  policyholderPhone?: string | null;
  propertyAddress?: string | null;
  propertyCity?: string | null;
  propertyState?: string | null;
  carrier?: { name?: string | null } | null;
  insuranceNotified?: boolean | null;
  inspectionStatus?: string | null;
  inspectionDate?: string | null;
  statuteLimitationsDate?: string | null;
  depreciationRecoveryDeadline?: string | null;
  appraisalDeadlineDate?: string | null;
}

export interface FirstClaimDocument {
  id: string;
  fileName: string;
  displayName?: string | null;
  documentType?: string | null;
}

export interface FirstClaimEmail {
  id: string;
  subject?: string | null;
  senderName?: string | null;
  senderEmail?: string | null;
  createdAt?: string | null;
}

export interface ExtractedDeadlineSummary {
  id: string;
  deadlineType: string;
  deadlineDate: string;
  description?: string | null;
}

export interface KeyDate {
  id: string;
  label: string;
  /** YYYY-MM-DD. */
  date: string;
  /** Found by ClaimHive in an email or document and still waiting for review. */
  needsReview: boolean;
}

export interface NextStep {
  id: string;
  text: string;
}

const MAX_NEXT_STEPS = 4;

const DEADLINE_LABELS: Record<string, string> = {
  proof_of_loss_submitted: 'Proof of loss due',
  carrier_response: 'Carrier response due',
  depreciation_recovery: 'Depreciation recovery deadline',
  statute_limitations: 'Suit limitation deadline',
  appraisal_deadline: 'Appraisal deadline',
};

const dateOnly = (value: string) => value.slice(0, 10);

export function policyholderName(claim: FirstClaimFields): string | null {
  const person = [claim.policyholderFirstName, claim.policyholderLastName].filter(Boolean).join(' ').trim();
  return person || claim.policyholderCompany?.trim() || null;
}

export function lossAddress(claim: FirstClaimFields): string | null {
  const place = [claim.propertyCity, claim.propertyState].filter(Boolean).join(', ');
  const full = [claim.propertyAddress, place].filter(Boolean).join(', ');
  return full || null;
}

/** Dates ClaimHive found or computed, soonest first. Date of loss is shown separately. */
export function keyDatesFor(claim: FirstClaimFields, pendingDeadlines: ExtractedDeadlineSummary[]): KeyDate[] {
  const dates: KeyDate[] = [];
  const fromClaim: Array<[string, string, string | null | undefined]> = [
    ['inspection', 'Inspection', claim.inspectionDate],
    ['depreciation', 'Depreciation recovery deadline', claim.depreciationRecoveryDeadline],
    ['appraisal', 'Appraisal deadline', claim.appraisalDeadlineDate],
    ['statute', 'Suit limitation deadline', claim.statuteLimitationsDate],
  ];
  for (const [id, label, value] of fromClaim) {
    if (value) dates.push({ id, label, date: dateOnly(value), needsReview: false });
  }
  for (const deadline of pendingDeadlines) {
    dates.push({
      id: `extracted-${deadline.id}`,
      label: deadline.description?.trim() || DEADLINE_LABELS[deadline.deadlineType] || 'Deadline',
      date: dateOnly(deadline.deadlineDate),
      needsReview: true,
    });
  }
  return dates.sort((a, b) => a.date.localeCompare(b.date));
}

/** Up to four steps, most useful first. */
export function suggestNextSteps(
  claim: FirstClaimFields,
  documents: FirstClaimDocument[],
  pendingDeadlines: ExtractedDeadlineSummary[],
): NextStep[] {
  const steps: NextStep[] = [];
  const add = (id: string, text: string) => steps.push({ id, text });

  if (pendingDeadlines.length > 0) {
    const count = pendingDeadlines.length;
    add('review-deadlines', `Review ${count} deadline${count === 1 ? '' : 's'} ClaimHive found in the emails and documents`);
  }
  if (!claim.policyholderEmail && !claim.policyholderPhone) {
    add('policyholder-contact', "Confirm the policyholder's phone number and email");
  }
  if (!claim.claimNumber) add('claim-number', "Add the carrier's claim number");
  if (!claim.carrier?.name) add('carrier', 'Confirm which carrier issued the policy');
  if (!documents.some((doc) => doc.documentType === 'policy' || doc.documentType === 'declaration_page')) {
    add('policy-document', 'Request the policy from the policyholder');
  }
  if (!claim.insuranceNotified) add('notify-carrier', 'Confirm the carrier has been notified of the loss');
  if (!claim.inspectionStatus || claim.inspectionStatus === 'not_scheduled') {
    add('inspection', 'Schedule the inspection with the policyholder');
  }
  if (steps.length === 0) add('review', 'Review the claim and assign a Public Adjuster');

  return steps.slice(0, MAX_NEXT_STEPS);
}
